import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useGetSavedBrandAssetUrls } from "./upload/useSaveBrandAssets";
import { useSubmit } from "@certn/api";
import { showToast } from "@certn/cascadia";
import { useTranslation } from "react-i18next";
import { components } from "@certn/api/generated-schemas/client-portal";
import { appendBucketToUrls } from "../../../../utils";
import {
  brandAssetDisplayUrl,
  useBrandAssetObjectUrls,
} from "./upload/brandAssetObjectUrls";

export type CobrandingUrlsResponse =
  components["schemas"]["CobrandingUrlsResponse"];

const BRAND_ASSET_URL_KEYS = [
  "org_logo_url",
  "org_icon_url",
  "org_icon_url_dark_mode",
  "org_logo_url_dark_mode",
] as const;

type BrandAssetUrlKey = (typeof BRAND_ASSET_URL_KEYS)[number];

interface BrandingContextValue {
  isLoading: boolean;
  error?: Error;
  isValidating?: boolean;
  brandingAssets: CobrandingUrlsResponse | undefined;
  processedBrandingAssets: CobrandingUrlsResponse | undefined;
  isSaving: boolean;
  isDirty: boolean;
  setBrandingAssets: (value: CobrandingUrlsResponse | undefined) => void;
  setAssetPreview: (key: BrandAssetUrlKey, url: string | null) => void;
  mutate: () => void;
  saveBrandAssetsFromValues: () => Promise<void>;
  whitelabelReport: boolean;
  setWhitelabelReport: (value: boolean) => void;
  isWhitelabelDisabled: boolean;
}

const BrandingContext = createContext<BrandingContextValue | undefined>(
  undefined
);

interface BrandingProviderProps {
  children: ReactNode;
}

export function BrandingProvider({ children }: BrandingProviderProps) {
  const { t } = useTranslation();
  const { data, isLoading, mutate, error, isValidating } =
    useGetSavedBrandAssetUrls();
  const [brandingAssets, setBrandingAssetsState] = useState<
    CobrandingUrlsResponse | undefined
  >(undefined);
  const [lastSavedAssets, setLastSavedAssets] = useState<
    CobrandingUrlsResponse | undefined
  >(undefined);
  const [whitelabelReport, setWhitelabelReport] = useState<boolean>(false);
  const [lastSavedWhitelabelReport, setLastSavedWhitelabelReport] =
    useState<boolean>(false);
  const [localPreviews, setLocalPreviews] = useState<
    Partial<Record<BrandAssetUrlKey, string>>
  >({});

  const brandingAssetsRef = useRef(brandingAssets);
  const lastSavedAssetsRef = useRef(lastSavedAssets);
  const whitelabelReportRef = useRef(whitelabelReport);
  const lastSavedWhitelabelReportRef = useRef(lastSavedWhitelabelReport);
  brandingAssetsRef.current = brandingAssets;
  lastSavedAssetsRef.current = lastSavedAssets;
  whitelabelReportRef.current = whitelabelReport;
  lastSavedWhitelabelReportRef.current = lastSavedWhitelabelReport;

  const { submit, isSubmitting } = useSubmit(
    "/api/internal/client-portal/customer-accounts/cobranding/",
    {
      showToastOnError: true,
      onSuccess: () => {
        showToast(t("Branding updated successfully"), { type: "success" });
        setLastSavedAssets(brandingAssets);
        setLastSavedWhitelabelReport(whitelabelReport);
        mutate();
      },
    }
  );

  const saveBrandAssets = useCallback(
    async ({
      logoKey,
      iconKey,
      darkModeLogoKey,
      darkModeIconKey,
      whitelabelReportValue,
    }: {
      logoKey?: string | null;
      iconKey?: string | null;
      darkModeLogoKey?: string | null;
      darkModeIconKey?: string | null;
      whitelabelReportValue: boolean;
    }) => {
      const payload: Record<string, string | boolean> = {};

      payload.org_logo_key = logoKey || "";
      payload.org_icon_key = iconKey || "";
      payload.org_logo_key_dark_mode = darkModeLogoKey || "";
      payload.org_icon_key_dark_mode = darkModeIconKey || "";
      payload.whitelabel_report = whitelabelReportValue;

      await submit(payload);
    },
    [submit]
  );

  const saveBrandAssetsFromValues = useCallback(async () => {
    return await saveBrandAssets({
      logoKey: brandingAssets?.org_logo_url,
      iconKey: brandingAssets?.org_icon_url,
      darkModeIconKey: brandingAssets?.org_icon_url_dark_mode,
      darkModeLogoKey: brandingAssets?.org_logo_url_dark_mode,
      whitelabelReportValue: whitelabelReport,
    });
  }, [saveBrandAssets, brandingAssets, whitelabelReport]);

  // Wrapper that automatically manages whitelabel_report based on logo presence
  // Note: Only the regular logo (org_logo_url) is used for PDF reports, not the dark mode logo
  const setBrandingAssets = useCallback(
    (value: CobrandingUrlsResponse | undefined) => {
      const previousLogoUrl = brandingAssets?.org_logo_url;
      const newLogoUrl = value?.org_logo_url;

      // Auto-enable whitelabel_report when a logo is uploaded
      if (!previousLogoUrl && newLogoUrl) {
        setWhitelabelReport(true);
      }

      // Auto-disable whitelabel_report when the logo is removed
      if (previousLogoUrl && !newLogoUrl) {
        setWhitelabelReport(false);
      }

      setBrandingAssetsState(value);
      setLocalPreviews((current) => {
        const next = { ...current };
        let hasChanged = false;
        for (const key of BRAND_ASSET_URL_KEYS) {
          if (!value?.[key] && next[key]) {
            delete next[key];
            hasChanged = true;
          }
        }
        return hasChanged ? next : current;
      });
    },
    [brandingAssets]
  );

  const setAssetPreview = useCallback(
    (key: BrandAssetUrlKey, url: string | null) => {
      setLocalPreviews((current) => {
        if (!url) {
          if (!current[key]) {
            return current;
          }
          const next = { ...current };
          delete next[key];
          return next;
        }
        return { ...current, [key]: url };
      });
    },
    []
  );

  // Whitelabel is disabled when there's no logo uploaded (only regular logo is used for PDFs)
  const isWhitelabelDisabled = !brandingAssets?.org_logo_url;
  const storedObjectUrls = useBrandAssetObjectUrls();

  const value: BrandingContextValue = {
    isLoading,
    error,
    isValidating,
    brandingAssets,
    processedBrandingAssets: useMemo(() => {
      const processed = appendBucketToUrls(brandingAssets);
      if (!processed) {
        return processed;
      }
      const withPreviews = { ...processed };
      for (const key of BRAND_ASSET_URL_KEYS) {
        const remoteUrl = processed[key];
        if (!remoteUrl) {
          continue;
        }
        withPreviews[key] =
          localPreviews[key] ??
          brandAssetDisplayUrl(remoteUrl, storedObjectUrls) ??
          remoteUrl;
      }
      return withPreviews;
    }, [brandingAssets, localPreviews, storedObjectUrls]),
    isSaving: isSubmitting,
    isDirty: useMemo(() => {
      const keys: Array<
        keyof Omit<
          CobrandingUrlsResponse,
          | "bucket"
          | "whitelabel_report"
          | "primary_brand_color"
          | "optimal_text_color"
        >
      > = [
        "org_logo_url",
        "org_icon_url",
        "org_icon_url_dark_mode",
        "org_logo_url_dark_mode",
      ];
      const normalize = (value: string | null | undefined) => value ?? "";
      const current: Partial<CobrandingUrlsResponse> = brandingAssets ?? {};
      const baseline: Partial<CobrandingUrlsResponse> = lastSavedAssets ?? {};
      const assetsChanged = keys.some(
        (key) =>
          normalize(current[key] as string | null | undefined) !==
          normalize(baseline[key] as string | null | undefined)
      );
      const whitelabelChanged = whitelabelReport !== lastSavedWhitelabelReport;
      return assetsChanged || whitelabelChanged;
    }, [
      brandingAssets,
      lastSavedAssets,
      whitelabelReport,
      lastSavedWhitelabelReport,
    ]),
    setBrandingAssets,
    setAssetPreview,
    mutate,
    saveBrandAssetsFromValues,
    whitelabelReport,
    setWhitelabelReport,
    isWhitelabelDisabled,
  };

  useEffect(() => {
    if (data) {
      const filteredData = Object.entries(data).reduce<CobrandingUrlsResponse>(
        (acc, [key, value]) => {
          if (
            key === "org_logo_url" ||
            key === "org_icon_url" ||
            key === "org_icon_url_dark_mode" ||
            key === "org_logo_url_dark_mode"
          ) {
            if (typeof value === "string" && value.includes("client_logos")) {
              const clientLogosIndex = value.indexOf("client_logos");
              acc[key] = value.substring(clientLogosIndex) as any;
            } else {
              acc[key] = (value as string | null) ?? null;
            }
          } else if (key === "primary_brand_color") {
            acc.primary_brand_color = (value as string | null) ?? null;
          } else if (key === "optimal_text_color") {
            acc.optimal_text_color = (value as string | null) ?? null;
          } else if (key === "whitelabel_report") {
            acc.whitelabel_report = Boolean(value);
          } else if (key === "bucket" && typeof value === "string") {
            acc.bucket = value;
          }
          return acc;
        },
        {
          bucket: "",
          org_logo_url: null,
          org_icon_url: null,
          org_icon_url_dark_mode: null,
          org_logo_url_dark_mode: null,
          primary_brand_color: null,
          optimal_text_color: null,
          whitelabel_report: false,
        }
      );
      const normalize = (value: string | null | undefined) => value ?? "";
      const hasUnsavedChanges =
        BRAND_ASSET_URL_KEYS.some(
          (key) =>
            normalize(brandingAssetsRef.current?.[key]) !==
            normalize(lastSavedAssetsRef.current?.[key])
        ) ||
        whitelabelReportRef.current !== lastSavedWhitelabelReportRef.current;

      if (!hasUnsavedChanges) {
        setBrandingAssetsState(filteredData);
        setLastSavedAssets(filteredData);

        // Initialize whitelabel_report from API response
        // Only enable if there's actually a logo uploaded (can't whitelabel without a logo)
        const hasLogo = Boolean(filteredData.org_logo_url);
        const apiWhitelabelValue =
          "whitelabel_report" in data ? (data as any).whitelabel_report : false;
        const whitelabelValue = hasLogo && apiWhitelabelValue;
        setWhitelabelReport(whitelabelValue);
        setLastSavedWhitelabelReport(whitelabelValue);
      }
    } else {
      setBrandingAssetsState(data);
      setLastSavedAssets(data);
    }
  }, [data]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBrandingContext() {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error(
      "useBrandingContext must be used within a BrandingProvider"
    );
  }
  return context;
}
